import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    resource,
    ResourceRef,
    signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { AuthorizationService } from '@pure/authz-authorizer';
import {
    AppNavigationPopupActionComponent,
    AppNavigationPopupContentDirective,
    ButtonComponent,
    LabelSkeletonComponent,
    LoadIndicatorComponent,
} from '@pure/hive';
import { firstValueFrom } from 'rxjs';
import { SMART_TIMER } from '../../arrays/appliances-view/smart-timer';
import { ampli } from '../../ampli';
import { FusionStatusComponent } from '../fusion-status/fusion-status.component';
import { FusionFleetStatusService } from '../fleet/services/fusion-fleet-status.service';

@Component({
    selector: 'fusion-status-menu-item',
    imports: [
        AppNavigationPopupActionComponent,
        AppNavigationPopupContentDirective,
        NgTemplateOutlet,
        ButtonComponent,
        LoadIndicatorComponent,
        FusionStatusComponent,
        RouterLink,
        LabelSkeletonComponent,
    ],
    templateUrl: './fusion-status-menu-item.component.html',
    styleUrl: './fusion-status-menu-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FusionStatusMenuItemComponent {
    readonly collapsed = input(false);
    private readonly smartTimer = inject(SMART_TIMER);
    private readonly refreshTick = toSignal(this.smartTimer(60000, 60000), { initialValue: -1 });
    readonly initialLoad = signal(true);
    private readonly lastSuccessfulFusionStatus = signal<{ total: number; inFleet: number } | undefined>(undefined);

    constructor(
        private readonly authorizationService: AuthorizationService,
        private readonly fusionFleetStatusService: FusionFleetStatusService,
    ) {}

    isAuthorized = toSignal(this.authorizationService.isAllowed('FUSION:navigate:fleet_administration'));
    readonly displayedFusionStatus = computed(() => this.fusionStatus.value() ?? this.lastSuccessfulFusionStatus());
    fusionStatus: ResourceRef<{ total: number; inFleet: number } | undefined> = resource({
        params: () => ({
            isAuthorized: this.isAuthorized(),
            refreshTick: this.refreshTick(),
        }),
        loader: async ({ params }) => {
            if (!params.isAuthorized) {
                return { total: 0, inFleet: 0 };
            }
            const fleetStatus = await firstValueFrom(this.fusionFleetStatusService.getStatus());
            const status = {
                total: fleetStatus.totalCount,
                inFleet: fleetStatus.inFleetCount,
            };
            this.lastSuccessfulFusionStatus.set(status);
            this.initialLoad.update(() => false);
            return status;
        },
    });
    protected readonly ampli = ampli;
}
